import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { cloneDeep, isEqual } from 'lodash';
import type { SurgicalGuideState } from './surgicalGuide.types';
import { initialState, initialSurgicalGuideData } from '@/stores/surgicalGuide/surgicalGuideInitialState';
import { makeDataState, withStateLoading, withStateSaving } from '@/util/dataState';
import type { AxiosRequestConfig } from 'axios';
import {
    loadSurgicalGuideUserPreferences,
    saveSurgicalGuidePreferences,
} from '@/api/surgicalGuide/services';

function surgicalGuideState(): SurgicalGuideState {
    return {
        ...cloneDeep(initialState),
    };
}

export const useSurgicalGuidePreferences = defineStore('surgical-guide-user-preferences', () => {
    const _state = reactive<SurgicalGuideState>(surgicalGuideState());

    const hasUnsavedChanges = computed(() => !isEqual(_state.lastSaved, _state.current));

    // Reset in place so the `current` reference exposed by the store stays
    // valid (mirrors caseStore). Object.assign-ing a fresh object onto `_state`
    // would strand any holder of the previous `current`.
    const _clearState = () => {
        Object.assign(_state.current, initialSurgicalGuideData);
        Object.assign(_state.lastSaved, initialSurgicalGuideData);
        Object.assign(_state, makeDataState());
    };

    const load = async (userId: number, config?: AxiosRequestConfig): Promise<void> => {
        _clearState();

        await withStateLoading(_state, async () => {
            const preferencesData = await loadSurgicalGuideUserPreferences(userId, config);

            if (preferencesData) {
                Object.assign(_state.current, preferencesData);
            }

            Object.assign(_state.lastSaved, cloneDeep(_state.current));
        }, config?.signal);
    };

    const save = async (userId: number, config?: AxiosRequestConfig): Promise<void> => {
        await withStateSaving(_state, async () => {
            await saveSurgicalGuidePreferences(userId, _state.current, config);

            Object.assign(_state.lastSaved, cloneDeep(_state.current));
        }, config?.signal);
    };

    return {
        current: _state.current,
        hasUnsavedChanges,
        $reset: _clearState,
        load,
        save,
    };
});
